package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.pages.LearnBookRatingStyle
import com.corbettcode.allaboutrocks.pages.LearnBookTitleStyle
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 600.px
private val FIGURE_IMAGE_MAX_WIDTH = 600.px
private val BOOK_COVER_MAX_WIDTH = 400.px

@InitRoute
fun initTypesOfCarnelian(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** What to check when you think you have a piece. */
private val carnelianKeyCharacteristics = listOf(
    "Translucency" to "The trait that defines it. Hold a piece up to a lamp or the sun and light passes through, at least at the thin edges, and the stone glows orange-red. An opaque red stone is jasper, not carnelian."
    , "Color" to "Pale orange through to deep brownish red, all of it from iron oxide. Natural material is usually uneven — cloudy, streaky, or graded from one end of the piece to the other."
    , "Hardness" to "6.5–7 on the Mohs scale. It scratches glass easily, resists scratching by a steel knife, and takes an excellent polish."
    , "Fracture" to "Conchoidal, breaking in smooth curved surfaces with edges sharp enough to cut. There is no cleavage."
    , "Luster" to "Waxy to greasy on a broken surface, and distinctly glassy once polished."
    , "Texture" to "Cryptocrystalline — the quartz crystals are far too small to see, so the stone looks solid and smooth rather than sparkly."
)

/** Carnelian's near relatives, and the names you will see on labels. */
private val carnelianVarieties = listOf(
    "Carnelian" to "The red to orange end of the range, translucent, with color from iron oxide spread through the chalcedony."
    , "Sard" to "The darker sibling — brown to brownish-red, and often a little harder and tougher. The line between sard and carnelian is a matter of taste rather than chemistry, and dealers draw it in different places."
    , "Sardonyx" to "Sard banded with white or pale layers. Those flat parallel bands are what makes it the classic material for cameos, since a carver can cut a white figure out of the top layer against a red ground."
    , "Carnelian agate" to "Material where the red is banded rather than evenly spread. It sits on the boundary between carnelian and agate, and either name is defensible."
    , "Heat-treated chalcedony" to "A large share of the carnelian sold as beads and cabochons is pale chalcedony or agate that has been heated to deepen the color. It is a legitimate, ancient treatment, but worth knowing about."
)

/** The colors you will find, and what is behind each one. */
private val carnelianOrigins = listOf(
    "Silica fills a cavity" to "Silica-rich water works through gas bubbles, cracks, and voids in volcanic rock and deposits chalcedony on the walls, layer by layer, until the space is lined or filled."
    , "Iron does the coloring" to "Iron oxide and hydroxide particles scattered through the silica give the orange and red. More iron and more oxidation means a deeper, browner color."
    , "Basalt is the usual host" to "The great deposits sit in weathered basalt flows — the Deccan Traps of India, the flood basalts of Brazil and Uruguay, and the volcanic country of the American West."
    , "Then it weathers out" to "Chalcedony is harder and tougher than the basalt around it, so nodules survive erosion and end up as loose pebbles in stream gravel and on beaches, which is where most collectors find them."
)

/** Outside references, paired with what each one is actually good for. */
private val carnelianFurtherReading = listOf(
    Triple(
        "Wikipedia: Carnelian"
        , "The general overview, and the same article covers sard — the two names have been used loosely enough that Wikipedia treats them together."
        , "https://en.wikipedia.org/wiki/Carnelian"
    )
    , Triple(
        "Mindat: Carnelian"
        , "Mineral data and a locality database, including the New Jersey and Oregon occurrences that turn up most often in North American collections."
        , "https://www.mindat.org/min-9333.html"
    )
    , Triple(
        "Geology.com: Carnelian"
        , "Photograph-heavy coverage of carnelian as a gem material, with a useful section on heat treatment."
        , "https://geology.com/gemstones/carnelian/"
    )
    , Triple(
        "Minerals.net: Carnelian"
        , "Carnelian set in context as one chalcedony variety among many, which is the clearest way to see where the boundaries sit."
        , "https://www.minerals.net/mineral/carnelian.aspx"
    )
    , Triple(
        "Wikipedia: Onyx"
        , "Covers sardonyx, the banded carnelian-and-white stone that has been the material of choice for carved cameos since antiquity."
        , "https://en.wikipedia.org/wiki/Onyx"
    )
)

@Composable
private fun CarnelianFigure(src: String, alt: String, caption: String, credit: String) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = src
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
        SpanText(credit, ImageCreditStyle.toModifier())
    }
}

@Composable
private fun CarnelianSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfCarnelianHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Carnelian"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfCarnelianHero() {

    TypesOfCarnelianHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/carnelian/carnelian_backlit.jpg"
                    , alt = "Backlit piece of orange carnelian glowing with transmitted light"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("Natural carnelian from Stirling Brook, Somerset County, New Jersey, lit from behind — the glow is the test that separates carnelian from jasper.") } }
                SpanText(
                    "Photo: Kelly Nash, CC BY 4.0, via Wikimedia Commons"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Carnelian is the translucent red-orange variety of chalcedony — the same cryptocrystalline quartz family as agate and jasper, colored through and through by iron oxide. It is one of the oldest worked gemstones on Earth, tough enough to survive a tumbler or a lifetime in a ring, and common enough that a patient afternoon on the right gravel bar can turn up a piece.")
            }

            CarnelianSectionTitle("How It Forms")
            P {
                Text("Carnelian is not a mineral in its own right. It is chalcedony that happened to pick up iron on the way in, and it forms the same way the rest of the family does:")
            }
        }
    }

    Column(
        HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Ul(PageListStyle.toAttrs()) {
            carnelianOrigins.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        CarnelianFigure(
            src = "/images/typesof/carnelian/carnelian_in_host_rock.jpg"
            , alt = "Red carnelian nodules sitting in a grey volcanic host rock"
            , caption = "Carnelian still in its host rock, Nová Paka, Czech Republic — silica filled the gas cavities in a volcanic flow, and iron turned the fill red."
            , credit = "Photo: Jan Helebrant, CC BY 2.0, via Wikimedia Commons"
        )
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        CarnelianSectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            carnelianKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        CarnelianFigure(
            src = "/images/typesof/carnelian/carnelian_agate_oregon.jpg"
            , alt = "Rough carnelian agate nodule from Oregon with a weathered rind"
            , caption = "A carnelian agate from Oregon, still carrying its weathered rind — in the field this is what you are actually looking for, not a polished stone."
            , credit = "Photo: Bobjgalindo, CC BY-SA 4.0, via Wikimedia Commons"
        )

        CarnelianSectionTitle("Carnelian, Sard, and Sardonyx")
        P {
            Text("These three names describe points along one continuous range rather than three different stones:")
        }

        Ul(PageListStyle.toAttrs()) {
            carnelianVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        CarnelianFigure(
            src = "/images/typesof/carnelian/sard_madagascar.jpg"
            , alt = "Polished brown sard with cloudy white and grey banding"
            , caption = "Polished sard from Madagascar — the darker brown end of the range, with pale cloudy bands running through it."
            , credit = "Photo: Plutos, CC0, via Wikimedia Commons"
        )

        CarnelianSectionTitle("Telling It from Jasper and Agate")
        P {
            Text("All three are the same chemistry, so the test is how light behaves rather than what the stone is made of. Hold the piece up to a strong light: carnelian glows orange-red, at least around the edges, because it is translucent. Jasper stays dark — it is opaque, packed with enough iron and clay to stop light entirely. Agate is translucent as well, but its color arrives in sharp parallel bands, while carnelian's is diffuse and cloudy. A stone can legitimately sit between these categories, and nobody will be able to tell you the one correct name for it.")
        }

        CarnelianSectionTitle("A Note on Heat Treatment")
        P {
            Text("Much of the carnelian in bead shops started out as ordinary pale chalcedony or agate and was baked to bring the color out. Heating converts yellowish iron hydroxides into red hematite, and the practice goes back thousands of years, so it is not a modern deception. Two things usually give treated material away: the color is suspiciously even across the whole stone, and where the original stone was banded, heating tends to sharpen those bands into stripes rather than the soft clouds you see in natural carnelian.")
        }

        CarnelianSectionTitle("Historical and Cultural Uses")
        P {
            Text("Carnelian has been carved for at least five thousand years. Craftsmen of the Indus Valley drilled long carnelian beads and decorated them with white designs, and those beads travelled west as trade goods, turning up in graves at Ur, Susa, and Kish. Egyptian workshops cut it into amulets and inlay, and Greek and Roman engravers favored it for intaglio seal stones and signet rings — the traditional explanation being that warm sealing wax lets go of a polished carnelian face cleanly. It remained a standard stone for rings and seals across the Islamic world long after.")
        }

        CarnelianFigure(
            src = "/images/typesof/carnelian/indus_etched_carnelian_bead.jpg"
            , alt = "Ancient carnelian bead decorated with white ring designs"
            , caption = "An Indus Civilisation etched carnelian bead, about 2900–2350 BC, found at Nippur in Mesopotamia — carried some two thousand miles from where it was made."
            , credit = "Photo: The Metropolitan Museum of Art, CC0, via Wikimedia Commons"
        )

        CarnelianSectionTitle("Modern Uses")
        P {
            Text("Carnelian is a workhorse lapidary material. It cuts and polishes without drama, it is hard enough to wear daily, and it is forgiving enough that beginners do well with it — a batch of carnelian pebbles is about the most reliably satisfying thing you can put in a rock tumbler. Beyond tumbled stones it shows up as cabochons, beads, inlay, and carvings, and sardonyx is still the material of choice for carved cameos.")
        }

        CarnelianSectionTitle("Where to Find It")
        P {
            Text("India, Brazil, Uruguay, and Madagascar supply most of the world's commercial carnelian, all of it out of ancient basalt flows. In the United States it turns up across the volcanic West — Oregon beaches and desert washes, central Washington, Arizona, Nevada, and Utah — and in the old basalts of New Jersey. Look in stream gravel, on beach cobble, and in the wash below a basalt outcrop, and look with the sun in front of you rather than behind: carnelian pebbles are easy to walk past when they are dry and dusty, but they light up when the low sun catches them from the far side.")
        }

        CarnelianSectionTitle("For More Information")

        Ul(PageListStyle.toAttrs()) {
            carnelianFurtherReading.forEach { (title, description, url) ->
                Li {
                    B { Link(url, title) }
                    Text(" — $description")
                }
            }
        }
    }
}

@Composable
private fun TypesOfCarnelian_BooksSection() {
    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on Carnelian and Agate"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = booksOnCarnelianCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("/types_of_rocks/carnelian")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfCarnelianPage() {
    TypesOfCarnelianHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    TypesOfCarnelian_BooksSection()
}

/**
 * Carnelian has no book of its own, so the useful ones are the chalcedony and agate references.
 */
val booksOnCarnelianCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agate1.webp"
                , alt = "Agates: Treasures of the Earth"
                , modifier = Modifier
                    .maxWidth(BOOK_COVER_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column {
                SpanText(
                    "Agates: Treasures of the Earth"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "4.0 on Goodreads"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("Carnelian does not have a book to itself, and it does not need one — it is chalcedony, so the agate references cover it. This one provides a very well-rounded overview of the subject in a way that I haven’t seen done anywhere else. From the formation of agates to how they have been used throughout history, the book covers it all and would make an excellent addition to someone’s library or as a gift for someone who loves agates. It walks to the line of being technical but doesn’t cross it, which makes the material very digestible for the amateur collector.")
                }
            }
        }
    }
)
